import {
  WbAlert,
  WbButton,
  WbDrawerActions,
  WbDrawerContent,
  WbSelect,
  WbUsersAndGroupsPicker,
  useWbDrawerContext,
} from '@agilelab/plugin-wb-platform';
import { makeStyles, Typography } from '@material-ui/core';
import { useCallback, useMemo } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { TeamRoleAssignmentKindEnum } from '../../../types';
import { useCreateTeamRoleAssignment } from '../../../hooks';
import { useTeamRolesPanelContext } from '../../../context';
import { TeamRoleEnum } from '@agilelab/plugin-wb-team-roles-common';

const useStyles = makeStyles(
  {
    form: {
      display: 'flex',
      flexDirection: 'column',
      gap: '24px',
      marginTop: 24,
    },
  },
  { name: 'CreateTeamRoleAssignmentForm' },
);

const CreateTeamRoleAssignmentFormSchema = z.object({
  assignmentType: z.string().min(1, 'Assignment type is required'),
  usersAndGroups: z
    .array(z.string())
    .min(1, 'At least one user or group is required'),
});

type CreateTeamRoleAssignmentFormType = z.infer<
  typeof CreateTeamRoleAssignmentFormSchema
>;

const getDisabledKind = (
  assignmentType: string,
  enabledTeamRoles: any[],
  selectedTeamRole: TeamRoleEnum,
): 'user' | 'group' | undefined => {
  const enabledTeamRole = enabledTeamRoles.find(
    role => role.name === selectedTeamRole,
  );

  if (!enabledTeamRole) return undefined;

  switch (assignmentType) {
    case TeamRoleAssignmentKindEnum.FULL:
      if (!enabledTeamRole?.assigneeKind) return undefined;
      return enabledTeamRole.assigneeKind.toLowerCase() === 'group'
        ? 'user'
        : 'group';
    case TeamRoleAssignmentKindEnum.LIMITED:
      if (!enabledTeamRole.limitedAssigneeKind) return undefined;
      return enabledTeamRole.limitedAssigneeKind.toLowerCase() === 'group'
        ? 'user'
        : 'group';
    default:
      return undefined;
  }
};

const getAvailableAssignmentTypes = (
  hasTroubleshootAccess: boolean,
  selectedTeamRole: TeamRoleEnum,
  isLimitedOwner: boolean,
  isEmptyAssignments: boolean,
  isOwner: boolean,
  canHaveLimitedAssignees: boolean,
): TeamRoleAssignmentKindEnum[] => {
  if (hasTroubleshootAccess) {
    const types = [TeamRoleAssignmentKindEnum.FULL];
    if (canHaveLimitedAssignees) {
      types.push(TeamRoleAssignmentKindEnum.LIMITED);
    }
    return types;
  }

  if (selectedTeamRole === TeamRoleEnum.OWNER && isLimitedOwner) {
    return canHaveLimitedAssignees ? [TeamRoleAssignmentKindEnum.LIMITED] : [];
  }

  if (
    selectedTeamRole === TeamRoleEnum.OWNER &&
    isEmptyAssignments &&
    isOwner
  ) {
    return [TeamRoleAssignmentKindEnum.FULL];
  }

  if (selectedTeamRole !== TeamRoleEnum.OWNER && isEmptyAssignments) {
    return [TeamRoleAssignmentKindEnum.FULL];
  }

  const types = [TeamRoleAssignmentKindEnum.FULL];
  if (canHaveLimitedAssignees) {
    types.push(TeamRoleAssignmentKindEnum.LIMITED);
  }
  return types;
};

export const CreateTeamRoleAssignmentForm = () => {
  const classes = useStyles();
  const { setOpen } = useWbDrawerContext();

  const {
    selectedTeamRole,
    enabledTeamRoles,
    projectUrn,
    assignments,
    isOwner,
    isLimitedOwner,
    hasTroubleshootAccess,
    canHaveLimitedAssignees,
  } = useTeamRolesPanelContext();

  const isEmptyAssignments = !assignments || assignments?.length === 0;

  const {
    isLoading: creating,
    error: createError,
    mutate: createFn,
  } = useCreateTeamRoleAssignment({ alertOnError: false });

  const onSubmit = useCallback<SubmitHandler<CreateTeamRoleAssignmentFormType>>(
    data => {
      createFn(
        {
          projectUrn,
          teamRole: selectedTeamRole,
          subject: data.usersAndGroups[0],
          assignmentType: data.assignmentType as TeamRoleAssignmentKindEnum,
        },
        {
          onSuccess: () => {
            setOpen(false);
          },
        },
      );
    },
    [createFn, projectUrn, selectedTeamRole, setOpen],
  );

  const availableAssignmentsType = useMemo(
    () =>
      getAvailableAssignmentTypes(
        hasTroubleshootAccess,
        selectedTeamRole,
        isLimitedOwner,
        isEmptyAssignments,
        isOwner,
        !!canHaveLimitedAssignees,
      ),
    [
      hasTroubleshootAccess,
      selectedTeamRole,
      isLimitedOwner,
      isEmptyAssignments,
      isOwner,
      canHaveLimitedAssignees,
    ],
  );

  const defaultAssignmentType = useMemo(() => {
    return availableAssignmentsType.length === 1
      ? availableAssignmentsType[0]
      : '';
  }, [availableAssignmentsType]);

  const form = useForm<CreateTeamRoleAssignmentFormType>({
    resolver: zodResolver(CreateTeamRoleAssignmentFormSchema),
    defaultValues: {
      assignmentType: defaultAssignmentType,
      usersAndGroups: [],
    },
  });

  const assignmentType = form.watch('assignmentType');
  const usersAndGroups = form.watch('usersAndGroups');

  const loading = creating || form.formState.isValidating;
  const submitEnabled = Boolean(assignmentType && usersAndGroups?.length > 0);

  const disabledUserAndGroups = useMemo(() => {
    return new Set(assignments?.map(assignment => assignment.subject.urn));
  }, [assignments]);

  const disabledKind = useMemo(
    () => getDisabledKind(assignmentType, enabledTeamRoles, selectedTeamRole),
    [assignmentType, enabledTeamRoles, selectedTeamRole],
  );

  const description = useMemo(() => {
    if (
      defaultAssignmentType === TeamRoleAssignmentKindEnum.FULL &&
      !canHaveLimitedAssignees
    ) {
      return `The person you add as assignee will receive a role with specific permissions and responsibilities.`;
    }

    if (defaultAssignmentType === TeamRoleAssignmentKindEnum.FULL) {
      return `You can only add a Full assignee for ${selectedTeamRole} because no Full assignee has been assigned yet. A Full assignee is required as the first role assignment to ensure proper accountability.`;
    }

    if (defaultAssignmentType === TeamRoleAssignmentKindEnum.LIMITED) {
      return `You can only add a Limited assignee for ${selectedTeamRole}. The person you assign will receive specific permissions and responsibilities for this role.`;
    }

    return `The person you add as assignee will receive a role with specific permissions and responsibilities. You can add a Full or Limited assignee`;
  }, [defaultAssignmentType, canHaveLimitedAssignees, selectedTeamRole]);

  return (
    <>
      <WbDrawerContent>
        <Typography variant="h4">Add new assignment</Typography>
        <Typography variant="body1">{description}</Typography>

        <form onSubmit={form.handleSubmit(onSubmit)} className={classes.form}>
          {createError instanceof Error && (
            <WbAlert
              variant="collapsible"
              message={{
                title: 'Error',
                type: 'error',
                content: <Typography>{createError.message}</Typography>,
              }}
            />
          )}
          {canHaveLimitedAssignees && (
            <Controller
              name="assignmentType"
              control={form.control}
              render={({ field, fieldState: { error } }) => (
                <WbSelect
                  fullWidth
                  id="assignment-type"
                  required
                  label="Assignment type"
                  placeholder="Choose the type of assignment"
                  disabled={!!defaultAssignmentType}
                  options={availableAssignmentsType}
                  value={field.value ?? defaultAssignmentType}
                  onChange={value => {
                    field.onChange(value || '');
                  }}
                  error={Boolean(error)}
                  helperText={error?.message || 'Select the assignment type'}
                />
              )}
            />
          )}
          <Controller
            name="usersAndGroups"
            control={form.control}
            render={({ field }) => (
              <WbUsersAndGroupsPicker
                required
                maxItems={1}
                value={field.value ?? []}
                onChange={field.onChange}
                disabled={!assignmentType}
                disabledKind={disabledKind}
                checkOptionDisabled={e => {
                  return disabledUserAndGroups.has(e);
                }}
              />
            )}
          />
        </form>
      </WbDrawerContent>
      <WbDrawerActions>
        <WbButton
          onClick={() => {
            setOpen(false);
          }}
          disabled={creating}
          variant="outlined"
          color="primary"
          label="Cancel"
        />
        <WbButton
          label="Assign"
          onClick={form.handleSubmit(onSubmit)}
          disabled={!submitEnabled}
          loading={loading}
          variant="contained"
          color="primary"
        />
      </WbDrawerActions>
    </>
  );
};
